// ── Learn Journey View ────────────────────────────────────────
// Replaces FlowchartView for Learn Mode with a linear path + focus panel.
// Left rail: ordered concept list with mastery rings.
// Main panel: concept detail or active LearnCard.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import LearnCard from './chat/LearnCard';
import OverviewMiniPlayer from './OverviewMiniPlayer';
import TelegramLinkModal from './TelegramLinkModal';
import { authFetch } from './api';
import { getNodeConfig } from './nodeConfig';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Mastery Ring (SVG) ──────────────────────────────────────
function MasteryRing({ mastery, size = 28 }) {
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(mastery / 10, 1);
  const dashArray = `${circumference * progress} ${circumference * (1 - progress)}`;
  const color = mastery >= 8 ? '#22c55e' : mastery >= 5 ? '#fbbf24' : mastery >= 1 ? '#ef4444' : '#374151';
  const bgColor = mastery >= 8 ? '#22c55e22' : '#374151';

  return (
    <svg width={size} height={size} className="learn-mastery-ring">
      <circle cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={bgColor} strokeWidth={3} />
      <circle cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={3}
        strokeDasharray={dashArray}
        strokeDashoffset={circumference * 0.25}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.4s ease' }} />
      {mastery >= 8 && (
        <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
          fill={color} fontSize={10} fontWeight={700}>✓</text>
      )}
      {mastery > 0 && mastery < 8 && (
        <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
          fill={color} fontSize={8} fontWeight={700}>{mastery}</text>
      )}
    </svg>
  );
}

// ── Concept Detail (no active learn loop) ────────────────────
function ConceptDetail({ concept, mastery, onStartLearn, cachedLesson }) {
  const [showLesson, setShowLesson] = useState(false);
  // Local TTS via Kokoro-82M. 'idle' | 'loading' | 'playing' | 'error'
  // audioRef holds the live <audio> element so the user can pause/replay.
  const [ttsState, setTtsState] = useState('idle');
  const [ttsError, setTtsError] = useState(null);
  const audioRef = React.useRef(null);
  // Telegram push state: idle | sending | ok | err
  const [tgPushState, setTgPushState] = useState('idle');
  const [tgPushMsg, setTgPushMsg] = useState(null);
  const [tgPushModalOpen, setTgPushModalOpen] = useState(false);
  const data = concept.data || concept;
  const config = getNodeConfig(data.type, data.dynamicConfig);
  const score = mastery?.score || 0;
  const statusLabel = score >= 8 ? 'Mastered' : score >= 5 ? 'Partial' : score >= 1 ? 'In Progress' : 'Not Started';
  const statusColor = score >= 8 ? '#22c55e' : score >= 5 ? '#fbbf24' : score >= 1 ? '#ef4444' : '#6b7280';

  // Build the lesson text fed to Kokoro. Falls back to concept reasoning if
  // no cached lesson exists yet. Caps at 8000 chars (matches server cap).
  const buildLessonText = () => {
    const parts = [];
    parts.push(data.label || '');
    if (cachedLesson?.explanation) parts.push(cachedLesson.explanation);
    else if (data.reasoning) parts.push(data.reasoning);
    if (cachedLesson?.example) parts.push(`Example: ${cachedLesson.example}`);
    if (cachedLesson?.analogy) parts.push(`Think of it like: ${cachedLesson.analogy}`);
    if (cachedLesson?.keyTakeaways?.length) {
      parts.push(`Key takeaways: ${cachedLesson.keyTakeaways.join('. ')}`);
    }
    return parts.join('. ').slice(0, 8000);
  };

  const handleListen = async () => {
    // Toggle off if already playing
    if (ttsState === 'playing' && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setTtsState('idle');
      return;
    }
    setTtsState('loading');
    setTtsError(null);
    try {
      const res = await authFetch(`${API_URL}/api/voice/tts-local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: buildLessonText(), voice: 'af_heart', speed: 1.0 }),
      });
      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(`HTTP ${res.status}: ${errBody.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setTtsState('idle'); URL.revokeObjectURL(url); };
      audio.onerror = () => { setTtsState('error'); setTtsError('Playback failed'); };
      setTtsState('playing');
      await audio.play();
    } catch (err) {
      setTtsState('error');
      setTtsError(err.message);
    }
  };

  // Cleanup on unmount
  React.useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  }, []);

  // Push lesson audio to user's linked Telegram chat. Server synthesises
  // WAV via Kokoro + Telegram sendAudio. If bot not linked yet, open the
  // inline link modal — on successful link we auto-retry the push so the
  // user doesn't have to click again.
  const doPushLessonToTelegram = async () => {
    setTgPushState('sending');
    setTgPushMsg(null);
    try {
      const res = await authFetch(`${API_URL}/api/integrations/telegram/send-lesson-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conceptLabel: data.label, text: buildLessonText() }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (/not linked/i.test(body.error || '')) {
          setTgPushState('idle');
          setTgPushMsg(null);
          setTgPushModalOpen(true);
          return;
        }
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      setTgPushState('ok');
      setTgPushMsg(`✓ Sent (${body.duration}s)`);
      setTimeout(() => { setTgPushState('idle'); setTgPushMsg(null); }, 4000);
    } catch (err) {
      setTgPushState('err');
      setTgPushMsg(`✗ ${err.message}`);
      setTimeout(() => { setTgPushState('idle'); setTgPushMsg(null); }, 6000);
    }
  };
  const handlePushToTelegram = async () => {
    if (tgPushState === 'sending') return;
    await doPushLessonToTelegram();
  };

  return (
    <div className="learn-journey-concept">
      <div className="learn-journey-concept-meta">
        <span className="learn-journey-concept-type" style={{ color: config.color, borderColor: `${config.color}44`, background: `${config.color}12` }}>
          {config.icon} {config.label}
        </span>
        <span className="learn-journey-concept-status" style={{ color: statusColor, borderColor: `${statusColor}44`, background: `${statusColor}12` }}>
          {score}/10 {statusLabel}
        </span>
      </div>

      <h2 className="learn-journey-concept-title">{data.label}</h2>

      {data.reasoning && (
        <p className="learn-journey-concept-reasoning">{data.reasoning}</p>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="learn-journey-start-btn" onClick={() => onStartLearn(concept.id, { probeMode: 'text' })}>
          {score >= 8 ? '↻ Review This Concept' : score > 0 ? '▶ Continue Learning' : '▶ Start Learning'}
        </button>
        <button
          className="learn-journey-start-btn"
          onClick={() => onStartLearn(concept.id, { probeMode: 'mcq' })}
          style={{ background: 'rgba(251,191,36,0.15)', color: '#fbbf24' }}
          title="Test yourself with multiple-choice questions"
        >
          📝 Quiz Me
        </button>
        {cachedLesson && (
          <button className="learn-journey-start-btn" style={{ background: 'rgba(167,139,250,0.15)', color: '#a78bfa' }} onClick={() => setShowLesson(v => !v)}>
            {showLesson ? '▲ Hide Lesson' : '📖 Review Lesson'}
          </button>
        )}
        {/* Local TTS via Kokoro-82M — Apache-2.0 model, runs in-process. */}
        <button
          className="learn-journey-start-btn"
          onClick={handleListen}
          disabled={ttsState === 'loading'}
          aria-label={ttsState === 'playing' ? 'Stop lesson audio' : 'Listen to lesson'}
          title={ttsState === 'error' ? `TTS failed: ${ttsError}` : 'Listen to lesson (local Kokoro TTS)'}
          style={{
            background: ttsState === 'playing' ? 'rgba(52,211,153,0.18)' : ttsState === 'error' ? 'rgba(248,113,113,0.18)' : 'rgba(56,189,248,0.15)',
            color: ttsState === 'playing' ? '#34d399' : ttsState === 'error' ? '#f87171' : '#38bdf8',
            cursor: ttsState === 'loading' ? 'wait' : 'pointer',
            opacity: ttsState === 'loading' ? 0.7 : 1,
          }}
        >
          {ttsState === 'loading' ? '⟳ Synthesizing…'
            : ttsState === 'playing' ? '⏹ Stop'
            : ttsState === 'error' ? '⚠ Retry'
            : '🔊 Listen to Lesson'}
        </button>
        {/* Push the same lesson audio to user's linked Telegram chat. */}
        <button
          className="learn-journey-start-btn"
          onClick={handlePushToTelegram}
          disabled={tgPushState === 'sending'}
          aria-label="Push lesson audio to Telegram"
          title={tgPushState === 'err' ? `Push failed: ${tgPushMsg}` : 'Send lesson audio to your linked Telegram chat'}
          style={{
            background: tgPushState === 'ok' ? 'rgba(52,211,153,0.18)' : tgPushState === 'err' ? 'rgba(248,113,113,0.18)' : 'rgba(167,139,250,0.15)',
            color: tgPushState === 'ok' ? '#34d399' : tgPushState === 'err' ? '#f87171' : '#a78bfa',
            cursor: tgPushState === 'sending' ? 'wait' : 'pointer',
            opacity: tgPushState === 'sending' ? 0.7 : 1,
          }}
        >
          {tgPushState === 'sending' ? '⟳ Sending…' : '📤 Push to Telegram'}
        </button>
        {tgPushMsg && (
          <span
            role="status"
            aria-live="polite"
            style={{ fontSize: 11, color: tgPushState === 'err' ? '#f87171' : '#34d399', alignSelf: 'center', fontFamily: 'var(--font-mono)' }}
          >
            {tgPushMsg}
          </span>
        )}
      </div>

      {showLesson && cachedLesson && (
        <div className="learn-journey-cached-lesson" style={{ marginTop: 16, padding: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 8, fontSize: 13, color: '#94a3b8', lineHeight: 1.7 }}>
          <div>{cachedLesson.explanation}</div>
          {cachedLesson.keyTakeaways?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <strong style={{ color: '#e2e8f0' }}>Key Takeaways:</strong>
              <ul style={{ margin: '4px 0 0 16px' }}>{cachedLesson.keyTakeaways.map((t, i) => <li key={i}>{t}</li>)}</ul>
            </div>
          )}
          {cachedLesson.example && (
            <div style={{ marginTop: 12 }}>
              <strong style={{ color: '#e2e8f0' }}>Example:</strong>
              <div style={{ marginTop: 4 }}>{cachedLesson.example}</div>
            </div>
          )}
          {cachedLesson.analogy && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(167,139,250,0.06)', borderRadius: 6, color: '#a78bfa' }}>
              <strong>Think of it like:</strong> {cachedLesson.analogy}
            </div>
          )}
        </div>
      )}
      {/* Inline Telegram link modal — opens when user hits Push but bot
          isn't linked yet. On success retries the push automatically. */}
      {tgPushModalOpen && (
        <TelegramLinkModal
          onClose={() => setTgPushModalOpen(false)}
          onLinked={() => { setTgPushModalOpen(false); doPushLessonToTelegram(); }}
        />
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────
export default function LearnJourneyView({
  displayNodes,
  learnStream,
  masteryMap = {},
  isLearning,
  onConceptClick,
  onStartLearn,
  onAction,
  mnemonicJobs = {},
  onGenerateVideo,
  onPlayVideo,
  lessonCache = {},
  onExploreDeeper,
  pushDrillRef,
  paperTitle,
  topic,
  sessionId,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [learnDrillStack, setLearnDrillStack] = useState([]); // [{ conceptId, conceptLabel, scrollPos }]

  // Detect paper sessions from node data (paperRef fields) as fallback for paperTitle
  const isPaperSession = useMemo(() => {
    if (paperTitle) return true;
    return displayNodes?.some(n => n.data?.paperRef || n.data?.type === 'argument' || n.data?.type === 'evidence' || n.data?.type === 'counterpoint');
  }, [paperTitle, displayNodes]);

  // Derive effective paper title from nodes if not explicitly set
  const effectivePaperTitle = paperTitle || (isPaperSession ? topic : null);

  // Overview audio state
  const [overviewState, setOverviewState] = useState('idle'); // idle | loading | ready | error
  const [overviewChapters, setOverviewChapters] = useState([]);
  const [overviewTitle, setOverviewTitle] = useState('');
  const [overviewProgress, setOverviewProgress] = useState(''); // loading status text
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const overviewAbortRef = useRef(null);

  // Telegram send-overview state
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [tgToast, setTgToast] = useState(null); // { kind: 'info' | 'ok' | 'err', msg }

  const doSendOverviewToTelegram = useCallback(async () => {
    if (!sessionId) return;
    setTgToast({ kind: 'info', msg: `Sending ${overviewChapters.length} chapters to Telegram…` });
    try {
      const res = await authFetch(`${API_URL}/api/integrations/telegram/send-overview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTgToast({ kind: 'ok', msg: `✓ Sent ${data.chaptersSent} chapters to Telegram` });
      setTimeout(() => setTgToast(null), 4000);
    } catch (e) {
      setTgToast({ kind: 'err', msg: `Send failed: ${e.message}` });
      setTimeout(() => setTgToast(null), 6000);
    }
  }, [sessionId, overviewChapters.length]);

  const handleSendToTelegram = useCallback(async () => {
    if (!sessionId) return;
    // Check link status; if not linked, open modal; else send directly.
    try {
      const res = await authFetch(`${API_URL}/api/integrations/telegram/status`);
      const data = await res.json();
      if (data.connected) {
        await doSendOverviewToTelegram();
      } else {
        setShowTelegramModal(true);
      }
    } catch {
      setShowTelegramModal(true);
    }
  }, [sessionId, doSendOverviewToTelegram]);

  // Restore cached overview audio from server on mount (persists across session switches)
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    authFetch(`${API_URL}/api/learn/overview-audio/${sessionId}`)
      .then(r => r.ok ? r.json() : null)
      .then(cached => {
        if (cancelled || !cached?.chapters?.length) return;
        setOverviewChapters(cached.chapters);
        setOverviewTitle(cached.title || effectivePaperTitle || topic || 'Overview');
        setOverviewState('ready');
        setShowMiniPlayer(true);
      })
      .catch(() => {}); // no cached audio — that's fine
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Expose a push function so external callers (e.g. chat drill) can update the breadcrumb
  useEffect(() => {
    if (pushDrillRef) {
      pushDrillRef.current = (conceptId, conceptLabel) => {
        setLearnDrillStack(prev => [...prev, { conceptId, conceptLabel }]);
      };
    }
  }, [pushDrillRef]);

  // Sort concepts into learning order (prerequisites first, then by difficulty/depth)
  const orderedConcepts = useMemo(() => {
    if (!displayNodes?.length) return [];

    const learnable = displayNodes.filter(n => {
      const t = n.data?.type;
      return t === 'concept' || t === 'prerequisite' || t === 'exercise' || t === 'milestone';
    });

    // Build depth map via BFS from seed
    const depthMap = new Map();
    const queue = [];
    displayNodes.forEach(n => {
      const pids = n.data?.parentIds || [];
      if (pids.length === 0 || n.data?.type === 'seed') {
        depthMap.set(n.id, 0);
        queue.push(n.id);
      }
    });
    while (queue.length) {
      const id = queue.shift();
      const depth = depthMap.get(id);
      displayNodes.forEach(n => {
        if ((n.data?.parentIds || []).includes(id) && !depthMap.has(n.id)) {
          depthMap.set(n.id, depth + 1);
          queue.push(n.id);
        }
      });
    }

    // Sort: type priority (prerequisite < concept < exercise < milestone), then depth, then difficulty
    const typePriority = { prerequisite: 0, concept: 1, exercise: 2, milestone: 3 };
    return [...learnable].sort((a, b) => {
      const ta = typePriority[a.data?.type] ?? 1;
      const tb = typePriority[b.data?.type] ?? 1;
      if (ta !== tb) return ta - tb;
      const da = depthMap.get(a.id) ?? 99;
      const db = depthMap.get(b.id) ?? 99;
      if (da !== db) return da - db;
      return (a.data?.difficulty || 1) - (b.data?.difficulty || 1);
    });
  }, [displayNodes]);

  // Auto-select first concept if none selected
  useEffect(() => {
    if (!selectedId && orderedConcepts.length > 0) {
      setSelectedId(orderedConcepts[0].id);
    }
  }, [orderedConcepts, selectedId]);

  // Auto-advance when learn loop changes concept
  useEffect(() => {
    if (learnStream?.conceptId) {
      setSelectedId(learnStream.conceptId);
    }
  }, [learnStream?.conceptId]);

  // Compute progress
  const { mastered, total } = useMemo(() => {
    const learnableConcepts = orderedConcepts.filter(n =>
      n.data?.type === 'concept' || n.data?.type === 'prerequisite'
    );
    const masteredCount = learnableConcepts.filter(n =>
      (masteryMap[n.id]?.score || 0) >= 8
    ).length;
    return { mastered: masteredCount, total: learnableConcepts.length };
  }, [orderedConcepts, masteryMap]);

  const pct = total > 0 ? Math.round((mastered / total) * 100) : 0;
  const selectedConcept = orderedConcepts.find(n => n.id === selectedId);
  const selectedIdx = orderedConcepts.findIndex(n => n.id === selectedId);

  const handleSelect = (concept) => {
    setSelectedId(concept.id);
    onConceptClick?.(concept.id);
  };

  const goPrev = () => {
    if (selectedIdx > 0) handleSelect(orderedConcepts[selectedIdx - 1]);
  };
  const goNext = () => {
    if (selectedIdx < orderedConcepts.length - 1) handleSelect(orderedConcepts[selectedIdx + 1]);
  };

  // Drill into a sub-concept — push current context, trigger sub-tree generation
  const handleExploreDeeper = (conceptId, conceptLabel) => {
    setLearnDrillStack(prev => [...prev, { conceptId, conceptLabel }]);
    onExploreDeeper?.(conceptId, conceptLabel);
  };

  // Jump back to a breadcrumb position
  const handleBreadcrumbJump = (index) => {
    if (index < 0) {
      // Jump to root
      setLearnDrillStack([]);
      onExploreDeeper?.('__root__');
    } else {
      const entry = learnDrillStack[index];
      setLearnDrillStack(prev => prev.slice(0, index + 1));
      onExploreDeeper?.(entry.conceptId, entry.conceptLabel, { jumpBack: true });
    }
  };

  // Wrap onAction to intercept learnExploreDeeper
  const handleAction = (btn) => {
    if (btn.actionType === 'learnExploreDeeper') {
      handleExploreDeeper(btn.conceptId, btn.conceptLabel);
      return;
    }
    onAction?.(btn);
  };

  // Generate overview audio via SSE
  const generateOverviewAudio = useCallback(async () => {
    if (overviewState === 'loading') return;
    setOverviewState('loading');
    setOverviewProgress('Generating overview script...');
    setOverviewChapters([]);

    const compactNodes = orderedConcepts.map(n => ({
      id: n.id,
      type: n.data?.type || n.type,
      label: n.data?.label || n.label,
      reasoning: n.data?.reasoning,
      paperRef: n.data?.paperRef,
    }));

    try {
      const controller = new AbortController();
      overviewAbortRef.current = controller;

      const res = await authFetch(`${API_URL}/api/learn/overview-audio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes: compactNodes, topic: topic || effectivePaperTitle || 'this topic', paperTitle: effectivePaperTitle, sessionId }),
        signal: controller.signal,
      });

      // Handle non-SSE error responses (400, 500, etc.)
      if (!res.ok) {
        let errMsg = `Server error (${res.status})`;
        try { const j = await res.json(); errMsg = j.error || errMsg; } catch {}
        setOverviewState('error');
        setOverviewProgress(errMsg);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const chaps = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') break;

          try {
            const evt = JSON.parse(payload);
            if (evt.error) {
              setOverviewState('error');
              setOverviewProgress(evt.error);
              return;
            }
            if (evt.stage === 'script') {
              setOverviewProgress(evt.detail || 'Generating overview script...');
            } else if (evt.stage === 'script_done') {
              setOverviewProgress(`Script ready — ${evt.chapterCount} chapters. Synthesizing audio...`);
            } else if (evt.stage === 'synthesizing') {
              setOverviewProgress(evt.detail);
            } else if (evt.stage === 'chapter_done') {
              chaps.push(evt.chapter);
              setOverviewChapters([...chaps]);
              setOverviewProgress(`${chaps.length} of ${chaps.length + (evt.chapterIndex < orderedConcepts.length ? 1 : 0)} chapters ready`);
            } else if (evt.stage === 'complete') {
              setOverviewTitle(evt.title || effectivePaperTitle || topic || 'Overview');
              setOverviewState('ready');
              setShowMiniPlayer(true);
            }
          } catch (e) { /* skip unparseable */ }
        }
      }

      if (chaps.length > 0) {
        setOverviewState(prev => prev !== 'error' ? 'ready' : prev);
        setShowMiniPlayer(true);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setOverviewState('error');
        setOverviewProgress(err.message);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedConcepts, topic, effectivePaperTitle]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { overviewAbortRef.current?.abort(); };
  }, []);

  return (
    <div className="learn-journey">
      {/* Left Rail */}
      <div className="learn-journey-rail">
        <div className="learn-journey-progress">
          <div className="learn-journey-progress-track">
            <div className="learn-journey-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <span className="learn-journey-progress-label">
            {mastered}/{total} concepts {pct === 100 ? 'mastered' : `(${pct}%)`}
          </span>
        </div>

        {/* Overview audio button — available for both paper and topic learn sessions
            (min 3 learnable concepts keeps it from showing on tiny/empty trees). */}
        {orderedConcepts.length >= 3 && (
          <div className="learn-overview-btn-wrap">
            {overviewState === 'idle' && (
              <button className="learn-overview-btn" onClick={generateOverviewAudio}>
                🎧 Listen to Overview
              </button>
            )}
            {overviewState === 'loading' && (
              <button className="learn-overview-btn loading" disabled>
                <span className="learn-overview-spinner" /> {overviewProgress}
              </button>
            )}
            {overviewState === 'ready' && !showMiniPlayer && (
              <button className="learn-overview-btn" onClick={() => setShowMiniPlayer(true)}>
                🎧 Resume Overview
              </button>
            )}
            {overviewState === 'error' && (
              <button className="learn-overview-btn error" onClick={generateOverviewAudio}>
                ⚠ Retry Overview
              </button>
            )}
          </div>
        )}

        <div className="learn-journey-stops">
          {orderedConcepts.map((concept, i) => {
            const score = masteryMap[concept.id]?.score || 0;
            const isMastered = score >= 8;
            const isActive = concept.id === selectedId;
            const isCurrentLearn = learnStream?.conceptId === concept.id;
            const config = getNodeConfig(concept.data?.type, concept.data?.dynamicConfig);

            return (
              <button
                key={concept.id}
                className={`learn-journey-stop ${isActive ? 'active' : ''} ${isMastered ? 'mastered' : ''} ${isCurrentLearn ? 'learning' : ''}`}
                onClick={() => handleSelect(concept)}
              >
                {/* Connector line */}
                {i > 0 && <div className="learn-journey-connector" />}

                <MasteryRing mastery={score} />

                <div className="learn-journey-stop-text">
                  <span className="learn-journey-stop-label">{concept.data?.label}</span>
                  <span className="learn-journey-stop-type" style={{ color: config.color }}>
                    {config.icon} {concept.data?.type}
                  </span>
                </div>

                {isCurrentLearn && <span className="learn-journey-pulse">●</span>}
                {mnemonicJobs[concept.id]?.status === 'complete' && (
                  <span className="learn-journey-video-icon" title="Video available">▶</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Panel */}
      <div className="learn-journey-main">
        {/* Breadcrumb bar for drill navigation */}
        {learnDrillStack.length > 0 && (
          <div className="learn-journey-breadcrumb">
            <button className="learn-breadcrumb-btn" onClick={() => handleBreadcrumbJump(-1)}>
              ← Root
            </button>
            {learnDrillStack.map((entry, i) => (
              <span key={entry.conceptId}>
                <span className="learn-breadcrumb-sep">›</span>
                <button
                  className="learn-breadcrumb-btn"
                  style={{ fontWeight: i === learnDrillStack.length - 1 ? 700 : 400 }}
                  onClick={() => handleBreadcrumbJump(i)}
                >
                  {entry.conceptLabel}
                </button>
              </span>
            ))}
          </div>
        )}

        {learnStream && learnStream.conceptId === selectedId ? (
          <LearnCard
            state={learnStream}
            onAction={handleAction}
            mnemonicJob={learnStream?.conceptId ? mnemonicJobs[learnStream.conceptId] : null}
            onGenerateVideo={onGenerateVideo}
            onPlayVideo={onPlayVideo}
            canDrill={!!onExploreDeeper}
          />
        ) : selectedConcept ? (
          <ConceptDetail
            concept={selectedConcept}
            mastery={masteryMap[selectedConcept.id]}
            onStartLearn={onStartLearn}
            cachedLesson={lessonCache[selectedConcept.id] || null}
          />
        ) : (
          <div className="learn-journey-empty">
            <span style={{ fontSize: 32, opacity: 0.3 }}>⧫</span>
            <p>Select a concept from the path to begin learning</p>
          </div>
        )}

        {/* Navigation */}
        <div className="learn-journey-nav">
          <button className="learn-journey-nav-btn" onClick={goPrev} disabled={selectedIdx <= 0}>
            ← Previous
          </button>
          <span className="learn-journey-nav-pos">
            {selectedIdx + 1} / {orderedConcepts.length}
          </span>
          <button className="learn-journey-nav-btn" onClick={goNext} disabled={selectedIdx >= orderedConcepts.length - 1}>
            Next →
          </button>
        </div>
      </div>

      {/* Persistent Mini-Player */}
      {showMiniPlayer && overviewChapters.length > 0 && (
        <OverviewMiniPlayer
          chapters={overviewChapters}
          overviewTitle={overviewTitle || effectivePaperTitle || topic}
          onClose={() => setShowMiniPlayer(false)}
          onSendToTelegram={handleSendToTelegram}
        />
      )}

      {/* Telegram link modal */}
      {showTelegramModal && (
        <TelegramLinkModal
          onClose={() => setShowTelegramModal(false)}
          onLinked={() => { setShowTelegramModal(false); doSendOverviewToTelegram(); }}
        />
      )}

      {/* Telegram send toast */}
      {tgToast && (
        <div className={`tg-toast tg-toast-${tgToast.kind}`} role="status">
          {tgToast.msg}
        </div>
      )}
    </div>
  );
}
